import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Unexa';
const BASE_DESCRIPTION = 'Unexa AI — Study in Australia. Compare 26,000+ courses, find migration agents, explore visa & PR pathways, jobs, housing, and join a trusted student community.';
const DEFAULT_OG_IMAGE = '/og-default.png';

export function SEOHead({ title, description, path, type = 'website', image, noindex = false, structuredData, children }) {
  const fullTitle = title ? `${title} | Unexa` : 'Unexa AI — Study in Australia | Courses, Visa, PR, Jobs, Housing & Community Platform';
  const desc = description || BASE_DESCRIPTION;
  const canonicalUrl = path ? `https://unexa.app${path}` : `https://unexa.app${typeof window !== 'undefined' ? window.location.pathname : '/'}`;
  const ogImage = image || DEFAULT_OG_IMAGE;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonicalUrl} />

      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}

      {children}
    </Helmet>
  );
}

// Pre-built structured data generators
export function universitySchema(uni) {
  return {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    name: uni.name,
    description: uni.description || `Study at ${uni.name} in ${uni.city}, ${uni.country}`,
    address: {
      '@type': 'PostalAddress',
      addressLocality: uni.city,
      addressCountry: uni.country,
    },
    ...(uni.logo_url && { logo: uni.logo_url }),
    ...(uni.ranking_qs && { 'schema:ranking': `QS World Ranking: #${uni.ranking_qs}` }),
  };
}

export function courseSchema(course) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: course.name,
    description: course.description || `${course.degree_type} in ${course.field_of_study} at ${course.university_name}`,
    provider: {
      '@type': 'EducationalOrganization',
      name: course.university_name,
    },
    educationalLevel: course.degree_type,
    ...(course.duration_months && { timeRequired: `P${course.duration_months}M` }),
    offers: {
      '@type': 'Offer',
      price: course.tuition_fee_per_year,
      priceCurrency: course.currency || 'USD',
      category: 'Tuition Fee Per Year',
    },
    locationCreated: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: course.city,
        addressCountry: course.country,
      },
    },
  };
}

export function organizationSchema() {
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Unexa',
      url: 'https://unexa.app',
      description: BASE_DESCRIPTION,
      logo: 'https://unexa.app/icon-192.png',
      sameAs: [
        'https://www.linkedin.com/company/unexa',
        'https://twitter.com/unexa_app',
        'https://instagram.com/unexa.app'
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Unexa',
      url: 'https://unexa.app',
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://unexa.app/explore?q={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    }
  ];
}

export function jobPostingSchema(job) {
  return {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: job.title,
    description: job.description || `${job.title} at ${job.company_name}`,
    datePosted: job.created_at,
    employmentType: (job.job_type || '').toUpperCase().replace(/ /g, '_'),
    hiringOrganization: { '@type': 'Organization', name: job.company_name || 'Employer' },
    jobLocation: {
      '@type': 'Place',
      address: { '@type': 'PostalAddress', addressLocality: job.city, addressCountry: 'Australia' },
    },
    ...(job.salary_max && {
      baseSalary: {
        '@type': 'MonetaryAmount', currency: 'AUD',
        value: { '@type': 'QuantitativeValue', value: job.salary_max, unitText: (job.salary_type || 'HOUR').toUpperCase() },
      },
    }),
  };
}

export function agentSchema(agent) {
  const loc = agent.location_display || agent.business_city || agent.business_country || 'Australia';
  const marn = agent.mara_number || '';
  const agency = agent.agency_name || agent.business_name;
  const hasStateCity = agent.business_city && agent.business_state;
  return {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: agency || agent.name,
    alternateName: agent.name,
    description: agent.meta_description || agent.bio || `MARA-registered migration agent${marn ? ' #' + marn : ''} based in ${loc}. Licensed Australian visa services.`,
    url: typeof window !== 'undefined' ? `${window.location.origin}/agents/${agent.user_id}` : `https://unexa.app/agents/${agent.user_id}`,
    serviceType: 'Migration Agent',
    areaServed: agent.countries_served?.length ? agent.countries_served.map(c => ({ '@type': 'Country', name: c })) : [{ '@type': 'Country', name: 'Australia' }],
    provider: {
      '@type': 'Person',
      name: agent.name,
      jobTitle: 'Registered Migration Agent',
      ...(marn && { identifier: { '@type': 'PropertyValue', propertyID: 'MARN', value: marn } }),
      knowsLanguage: agent.languages_spoken?.length ? agent.languages_spoken : ['English'],
    },
    ...(hasStateCity && {
      address: {
        '@type': 'PostalAddress',
        addressLocality: agent.business_city,
        addressRegion: agent.business_state,
        addressCountry: agent.business_country || 'Australia',
      },
    }),
    ...(agent.profile_picture && { image: agent.profile_picture }),
    ...(agent.avg_rating > 0 && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: agent.avg_rating,
        reviewCount: agent.total_reviews || 0,
      },
    }),
  };
}

export function rentalSchema(listing) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Accommodation',
    name: listing.title,
    description: listing.description || `${listing.property_type} in ${listing.suburb}, ${listing.city}`,
    address: {
      '@type': 'PostalAddress',
      addressLocality: listing.suburb || listing.city,
      addressRegion: listing.city,
      addressCountry: 'Australia',
    },
    ...(listing.price_per_week && {
      offers: { '@type': 'Offer', price: listing.price_per_week, priceCurrency: 'AUD', unitCode: 'WEE' },
    }),
    numberOfBedrooms: listing.bedrooms,
  };
}

export function visaProgramSchema(program) {
  return {
    '@context': 'https://schema.org',
    '@type': 'GovernmentService',
    name: program.visa_name,
    description: program.description || `${program.visa_name} — Australian visa program`,
    serviceType: 'Visa Application',
    areaServed: { '@type': 'Country', name: 'Australia' },
    ...(program.government_fee && {
      offers: { '@type': 'Offer', price: program.government_fee, priceCurrency: 'AUD' },
    }),
  };
}

export function breadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: `${window.location.origin}${item.path}`,
    })),
  };
}
